import {IQueuedPdfServerJob, IServerPDFQueryPayload, ISubmitPdfJobResponse} from '@modules/pdf'
import {ToastPDFJob} from '@modules/pdf/serverRenderedPDF/components'
import {withRouter} from 'react-router'
import {autorun} from 'mobx'
import {observer} from 'mobx-react'
import {observable, action, React, RouteComponentProps, computed, reaction, runInAction, lockr, _, IdleTimer, utils, Fragment, Popover2} from '@api'
import {AnchorButton, Toast, Toaster} from '@blueprintjs/core'
import {DashboardQueryParams, configs, ui, globalStores, ILoginConfig, CatalogStore} from '@stores'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {authStore, ILoginResult} from '@utils'
import {MyPopover2Store} from 'centric-ui-components'
import * as css from './SiteStore.css'
import { QueryClient } from 'react-query'
import { trimEnd } from 'lodash'

import(/* webpackChunkName: "blueprint" */ '@blueprintjs/core/lib/css/blueprint.css').then(() => {
  setTimeout(() => {
    // @ts-ignore
    site.blueprintCssLoaded = true
  }, 50)
})

interface MyProps extends Partial<RouteComponentProps> {
  onInit?: (store: SiteStore) => void
}

export interface Category1Definition {
  id
  name
  code
  category2s: Array<Category1Definition>
}

export interface Category2Definition {
  id
  name
  code
}

export interface SeasonDefinition {
  id: string
  name
  code
  category1s: Array<Category1Definition>
}

export interface ISeasonalStore {
  season: SeasonDefinition
  category1: Category1Definition
  category2: Category2Definition
}

export interface IExportImagesConfig {
  smallImageSize: number
  mediumImageSize: number
  largeImageSize: number
  originalImageSize: number
  thumbnailImageSize: number
  imageSize: number
}

export interface IExportListValue {
  products: boolean,
  materials: boolean,
  colors: boolean,
  hardware: boolean,
  styles: boolean,
  notes: boolean,
  activity: boolean,
  images: boolean,
  tags: boolean
}

export interface IExportVLPListValue {
  merchOptions: boolean,
  merchProducts: boolean,
  tags: boolean
}

export let site: SiteStore = null

@(withRouter as any)
@observer
export class SiteStore extends React.Component<MyProps> {
  @observable isWindowFocused = true
  @observable blueprintCssLoaded = false
  // Main app will set this if the user touches the screen ever
  @observable touchDevice = ('TouchEvent' in window) && ('ontouchstart' in window)
  @observable pdfEmbedLink: boolean = false

  reloadTimer = null

  // Set by react-idle-timer - https://www.npmjs.com/package/react-idle-timer
  @observable userIsIdle = false
  @observable.ref queryClient: QueryClient

  get documentAspectRatio() {
    const {width, height} = document.body.getBoundingClientRect()
    return width / height
  }

  @observable.ref seasons: SeasonDefinition[] = []
  @observable features = {
    sidebar: {plmSiteToggle: false}, vlp: {seasonSelectorInHeader: true}
  }
  //@observable blueprintCssLoaded = false
  @observable.ref currentOpenPopover: MyPopover2Store
  @observable.ref private _activePopover: Popover2<any>
  get activePopover() { return this._activePopover}

  set activePopover(v) {
    const {_activePopover} = this

    if (_activePopover) {
      _activePopover.reposition()
      if (_activePopover === v) return
      /***
       * Piyush Bhati - Popover within popover is not working because of this closeActivePopover
       */
      // this.closeActivePopovers()
    }

    this._activePopover = v

    // console.log({before: _activePopover?.targetElement, after: v?.targetElement})

    if (this._activePopover) {
      this._activePopover.reposition()
    }
  }

  @action closeActivePopovers = () => {
    const {activePopover} = this
    if (activePopover) {
      activePopover.props.onInteraction && activePopover.props.onInteraction(false)
      activePopover.setState({isOpen: false})
    }
  }

  get isShowingPopover() {
    return document.querySelector(`.bp3-overlay.bp3-overlay-open`) != null
  }

  @computed get queryParams(): Partial<DashboardQueryParams> {
    const {sidebar, deployments, flexLayout: {flexEnabled}} = globalStores
    return {
      //search: undefined, // searchText === '' ? undefined : searchText,
      ...(deployments?.queryParams ?? {})
      //f_loadMoreCatalog: loadMoreCatalog ? null : undefined
    }
  }

  //private updateCardSizeAttribute = _.throttle((m: number) => document.querySelector(`[data-type='CatalogRoot']`)[0].style.setProperty('--catalog-card-size-value-1', m.toString()), 5)

  // @disposeOnUnmount private onUpdateCardSizeAttribute = autorun(() => {
  //   const {settings: {cardSize}} = catalog
  //   this.updateCardSizeAttribute(cardSize)
  // })

  //  style={{'--columns': columnView  ? Math.max(1, groupCount) : columns} as any}
  //

  @observable.ref seasonal: { season?: SeasonDefinition; category1?: Category1Definition; category2?: Category1Definition }

  @observable autoCloseErrorTimer

  constructor(props) {
    super(props)
    site = this // singleton
    globalStores.site = site
    window['site'] = site

    props.onInit && props.onInit(this)

    synchronizeStores(() => {
      window.addEventListener('blur', () => this.isWindowFocused = false)
      window.addEventListener('focus', () => this.isWindowFocused = true)

      reaction(() => this.userIsIdle, idle => {
        if (!idle) { // && (!lastTokenVerifiedTimeEs || new Date().getTime() - lastTokenVerifiedTimeEs.getTime())) {
          // Todo - this is awkward, the user isn't warned as to why they've been logged out, and couldn't we renew the token instead?
          globalStores.authStore.verifyTokenNotExpired()
        }
      }, {name: 'Verify auth token is still valid when user interacts'})

      // observe(this, 'searchText', ({oldValue}: any) => {
      //   console.log(oldValue, this.searchText)
      // });

      const {body} = document
      body.setAttribute('data-touch', this.touchDevice.toString())

      autorun(() => {
        const {error} = this
        if (error) {
          runInAction(() => {
            this.autoCloseErrorTimer && clearTimeout(this.autoCloseErrorTimer)
            this.autoCloseErrorTimer = setTimeout(() => {
              this.error = null
            }, 5000)
          })
        }
      }, {name: 'Autohide error snackbar'})

      window.addEventListener('resize', () => {
        if (utils.isSafari) {
          this._updateVhThrottled_SafariOnly()
        }
      }, false)

      // Listen for postMessage events from popup child windows
      window.addEventListener('message', (event) => {
        this._onWindowMessage(event)
      })
      // Is never called
      // window.addEventListener("dragenter", function (e) {
      //   console.log('window.dragenter')
      //
      //   !dragDrop.isDragging && dragDrop.readDataTransfer(e.dataTransfer)
      //   e && e.preventDefault();
      // }, false)

      autorun(() => {
        const {es: {axiosServicesInstance}, deployments} = globalStores
        if (deployments.initialized) {
          runInAction(async () => {
            await this._getLoginConfig()
          })
        }
      })

      this.initialized = true
    })

    // Put the data attributes on the root body so we can style popovers (in portals)
    autorun(() => {
      const {dataAttributes} = this, {body} = document
      Object.keys(dataAttributes).forEach(attr => {
        const v = dataAttributes[attr]
        v ? body.setAttribute(attr, v) : body.removeAttribute(attr)
      })
    }, {name: `Add site data attributes to body tag`})

    document.onpaste = this.onDocumentPaste
  }

  @action onDocumentPaste = (e: ClipboardEvent) => {
    const {flexLayout: {activeTab, activeCatalogRoot}} = globalStores

    if (activeTab?.component === 'whiteboard') {
      activeTab.extraData.whiteboard.onDocumentPaste(e)
      return
    }

    const items = e.clipboardData.items

    const imageFiles = _.map(items, item => {
      if (item.kind === 'file') {
        return item.getAsFile()
      }
    }).filter(_.identity)

    activeCatalogRoot && activeCatalogRoot.catalog.onPasteImages(imageFiles)
  }

  _updateVhThrottled_SafariOnly = _.throttle(() => {
    let vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
  }, 100)

  @computed get dataAttributes() {
    return {}
  }

  @observable initialized = false

  render() {
    const {userIsIdle, onUserIdle, onUserActive} = this

    return <Fragment>
      <IdleTimer key='idle-timer' timeout={5000} onIdle={onUserIdle} onActive={onUserActive}/>
    </Fragment>
  }

  @action(`User idle`) private onUserIdle = () => this.userIsIdle = true
  @action(`User active`) private onUserActive = () => this.userIsIdle = false

  // Mediocre solution vs fixing underlying tooltip issues with newer material ui
  /*@disposeOnUnmount hideMaterialUiTooltipsOnIdle = autorun(() => {
    if (this.userIsIdle) {
      const tooltip = document.querySelector("body > [role='tooltip']") as HTMLDivElement;
      if (tooltip) {
        tooltip.style.display = 'none'
      }
    }
  }, {name: 'Hide material-ui tooltips when user is inactive'})*/

  @action reset = () => {
    globalStores.workspaces.current = null
  }

  @action forceReloadPage = () => {
    if (this.reloadTimer) { clearTimeout(this.reloadTimer) }
    window.location.reload()
  }

  @action initiateForceReloadTimerOnConfigChange = () => {
    this.reloadTimer = setTimeout(() => {
      window.location.reload()
    }, 10000)
  }

  @computed get loginTitle() {
    const {loginConfig, onSSOLogin_start} = this
    return <div id='login-title' className={css.loginTitle}>
      <span>{ui.settings.defaultProductName}</span>
      {loginConfig?.sso &&
	   <div className={css.sso}>
		   <AnchorButton icon='key' text='SSO Login' minimal onClick={onSSOLogin_start}/>
	   </div>}
    </div>
  }

  @observable sizes = {
    /**
     *  This height is dynamic and mutable as it can change as the user resizes the screen and forces wrapping */
    topBar: {height: ui.settings.titleBarHeight ?? 49},
    siteHeader: {height: ui.settings.titleBarHeight ?? 44},
    filterChips: {height: 0}
  }

  // region Seasonal - TODO move to separate store to deal with various hierarchies

  // @action onClickRefreshSeason = async () => {
  //   this.isRefreshingSeasons = !this.isRefreshingSeasons
  //   const {board} = this, {apollo: {client}, deployments: {deploymentConfig: {syncDevHierarchy}}, vlp:{ hierarchy:{loadPlansES, setPlanHierarchy }}, workspaces: {workspaceConfig}} = stores
  //   try {
  //     const {data: {syncUsers}} = await client.mutate({mutation: SYNC_USERS})
  //     if (syncUsers) {
  //       // TODO: will remove to sync dev hierarchy on refresh once implemented with C8 load
  //       syncDevHierarchy && await client.mutate({mutation: SYNC_DEV_HIERARCHY})
  //
  //       // Refetch plan
  //       if (workspaceConfig?.breadcrumbSource !== 'c8') {
  //         await client.mutate({mutation: SYNC_PLAN});
  //         await setPlanHierarchy(await loadPlansES())
  //       }
  //       site.showSnackbar = true
  //       site.message = 'Merchandising Board is refreshed successfully.'
  //     }
  //   } catch (err) {
  //     stores.onSiteError(err)
  //     // site.showSnackbar = true
  //     // site.message = 'unable to refresh user information'
  //   } finally {
  //     this.isRefreshingSeasons = false
  //   }
  // }

  // endregion

  canvas = null

  // region Error Handling

  @observable.ref error: Error
  @action onError = (err: any, showToast = true) => {
    if (err) {
      if (!_.isError(err)) {
        err = new Error(err)
      }

      DEBUG && console.error(err)

      // Error can come from anywhere, can't modify observables within computed
      setTimeout(() => {
        if (authStore.userIsAuthenticated) {
          showToast && (this.error = err)
        }
      }, 0)
    } else {
      this.error = null
    }
  }

  // endregion

  @observable.ref loginConfig: ILoginConfig
  @action _getLoginConfig = async () => {
    this.loginConfig = null
    const {es: {axiosServicesInstance: client}} = globalStores
    if (client) {
      try {
        // console.log(`Loading logininfo`)
        const {data} = await client.get(`/es/logininfo`)

        runInAction(() => {
          this.loginConfig = data
          const {loginConfig, stores: {config: {config}}} = this
          if (loginConfig?.elasticSearch) {
            const {elasticSearch: {url, apiKey}} = loginConfig
            config.esUrl = url
            config.esApiKey = apiKey
          }

          // console.log(`Loaded logininfo`)
        })
      } catch (err) {
        this.loginConfig = {}
        this.onError(err, false)
      }
    }
  }

  @action showToastMessage = (message: string | React.ReactNode) => {
    globalStores.catalog.dialogs.showToastMessage(message)
  }

  get stores() { return globalStores}

  @action onSSOLogin_start = () => {
    const {loginConfig: {sso}, stores: {nestJSUrl}} = this

    switch (sso.source) {
      case 'c8': {
        const redirect = encodeURIComponent(`${nestJSUrl}/auth/sso/callback`)

        // Using C8 SAML flow (3rd-party iDP should be configured in C8)
        const popupUrl = `${trimEnd(sso.c8Url, '/')}/csi-requesthandler/sso/idp-redirect?redirect=${redirect}`
        if (window) {
          // open popup centered over the current window          
          const { outerHeight, outerWidth, screenY, screenX } = window.top
          const w = 610, h = 720
          const y = screenY + 0.5 * (outerHeight - h)
          const x = screenX + 0.5 * (outerWidth - w)
          window.open(popupUrl, 'SSO', `popup,width=${w},height=${h},top=${y},left=${x}`)
        }
        break
      }
    }
  }

  // Handle window post messages
  _onWindowMessage = (event:MessageEvent) => {
    try {
      // If our payload is JSON that matches ILoginResult, pass it to authStore
      const payload = JSON.parse(event.data) as ILoginResult
      if (payload.access_token) {
        // console.log('[SiteStore] Got SSO payload', payload)
        authStore.onLoginSuccess(payload)
      }
    } catch (e) {}
  }

  @observable.ref toaster: Toaster
  onSetToaster = t => this.toaster = t

  @computed get openPdfToasts() {
    return (globalStores.cloudPDF?.pendingJobs ?? []).filter(j => j.hideToast !== true)
  }

  @computed get toasts() {
    const { openPdfToasts} = this
    return <Fragment>
      {openPdfToasts.map((job, i) => <ToastPDFJob key={job.toastId ?? job.jobId ?? i} job={job} />)}
    </Fragment>
  }
}

export const onSiteError = (error: any, showToast = true) => globalStores.site.onError(error, showToast)



// WEBPACK FOOTER //
// ./src/stores/site/SiteStore.tsx